import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { useMyBusinesses } from "@/hooks/use-businesses";
import { useAdminInviteUser } from "@/hooks/use-admin";
import { useAccountInviteAdmin } from "@/hooks/use-account-users";

type UserType = "admin" | "member";

const BUSINESS_ROLES = [
  { value: "admin", label: "Admin" },
  { value: "supervisor", label: "Supervisor" },
  { value: "member", label: "Member" },
  { value: "worker", label: "Worker" },
  { value: "viewer", label: "Viewer" },
] as const;

const END_USER_ROLES = [
  { value: "manager", label: "Manager" },
  { value: "salesperson", label: "Salesperson" },
  { value: "crew_chief", label: "Crew Chief" },
] as const;

interface InviteMemberSheetProps {
  open: boolean;
  onClose: () => void;
  accountId: string;
}

export function InviteMemberSheet({
  open,
  onClose,
  accountId,
}: InviteMemberSheetProps) {
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [userType, setUserType] = useState<UserType>("member");
  // ADV-1034: the per-invite admin role picker was removed — `inviteAdmin`
  // locks the role to `admin` server-side regardless of payload, and the
  // business-member path uses `account_role: "viewer"` (line below).
  const [businessRole, setBusinessRole] = useState("member");
  const [endUserRole, setEndUserRole] = useState("manager");
  const [selectedBusinessIds, setSelectedBusinessIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ADV-1029: Owner/Admin path uses the account-scoped /invite-admin endpoint.
  // The super-admin /admin/accounts/:id/invite path stays available for the
  // business-member invite (which carries business_ids + business_role) — that
  // payload shape isn't supported by the account-scoped endpoint yet. Tracked
  // separately as a follow-up: account-scoped business-member invites.
  const inviteUserAdminFallback = useAdminInviteUser();
  const inviteAdmin = useAccountInviteAdmin();
  const { data: businessesData } = useMyBusinesses();
  const businesses = businessesData?.businesses ?? [];

  function handleClose() {
    setEmail("");
    setFirstName("");
    setLastName("");
    setUserType("member");
    setBusinessRole("member");
    setEndUserRole("manager");
    setSelectedBusinessIds([]);
    setError(null);
    onClose();
  }

  function toggleBusiness(id: string, checked: boolean) {
    setSelectedBusinessIds((prev) =>
      checked ? [...prev, id] : prev.filter((b) => b !== id),
    );
  }

  async function handleSubmit() {
    setError(null);
    setSubmitting(true);
    try {
      // Admin invite (no business_ids): the account-scoped endpoint accepts
      // Owner/Admin callers and is the right path going forward. Role on the
      // server side is locked to `admin` regardless of payload (single-owner
      // invariant), so we don't send `account_role` here.
      if (userType === "admin") {
        await inviteAdmin.mutateAsync({
          email,
          first_name: firstName || undefined,
          last_name: lastName || undefined,
        });
      } else {
        // Business-member invite (with business_ids + business_role): the
        // super-admin endpoint is the only one that accepts this payload
        // today. Owner/Admin will 403 here until a follow-up adds an
        // account-scoped business-member invite path.
        await inviteUserAdminFallback.mutateAsync({
          accountId,
          data: {
            email,
            first_name: firstName || undefined,
            last_name: lastName || undefined,
            account_role: "viewer",
            ...(selectedBusinessIds.length > 0 && {
              business_ids: selectedBusinessIds,
              business_role: businessRole,
              end_user_role: endUserRole,
            }),
          },
        });
      }
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to invite member");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <SheetContent className="w-[480px] sm:max-w-[480px] flex flex-col">
        <SheetHeader>
          <SheetTitle>Invite Member</SheetTitle>
        </SheetHeader>

        <div className="flex-1 py-4 space-y-5 overflow-y-auto">
          {/* Email */}
          <div className="space-y-1.5">
            <label className="typo-label text-[color:var(--color-text-md)]">
              Email
            </label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
            />
          </div>

          {/* Name */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="typo-label text-[color:var(--color-text-md)]">
                First Name
              </label>
              <Input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Jane"
              />
            </div>
            <div className="space-y-1.5">
              <label className="typo-label text-[color:var(--color-text-md)]">
                Last Name
              </label>
              <Input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Smith"
              />
            </div>
          </div>

          {/* Access level toggle */}
          <div className="space-y-1.5">
            <label className="typo-label text-[color:var(--color-text-md)]">
              Access Level
            </label>
            <div className="rounded-md border border-[color:var(--color-border)] divide-y divide-[color:var(--color-border)]">
              <label className="flex items-start gap-3 p-3 cursor-pointer">
                <input
                  type="radio"
                  className="mt-0.5 accent-[color:var(--color-brand)]"
                  checked={userType === "admin"}
                  onChange={() => setUserType("admin")}
                />
                <div>
                  <p className="typo-body font-medium text-[color:var(--color-text-hi)]">
                    Account Admin
                  </p>
                  <p className="typo-body text-[color:var(--color-text-lo)]">
                    Admin role — full access to all businesses
                  </p>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 cursor-pointer">
                <input
                  type="radio"
                  className="mt-0.5 accent-[color:var(--color-brand)]"
                  checked={userType === "member"}
                  onChange={() => setUserType("member")}
                />
                <div>
                  <p className="typo-body font-medium text-[color:var(--color-text-hi)]">
                    Business Member
                  </p>
                  <p className="typo-body text-[color:var(--color-text-lo)]">
                    Restricted to specific businesses you assign
                  </p>
                </div>
              </label>
            </div>
          </div>

          {/* Business member fields */}
          {userType === "member" && (
            <>
              <div className="space-y-1.5">
                <label className="typo-label text-[color:var(--color-text-md)]">
                  Business Role
                </label>
                <Select value={businessRole} onValueChange={setBusinessRole}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {BUSINESS_ROLES.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <label className="typo-label text-[color:var(--color-text-md)]">
                  Functional Role
                </label>
                <Select value={endUserRole} onValueChange={setEndUserRole}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {END_USER_ROLES.map((r) => (
                      <SelectItem key={r.value} value={r.value}>
                        {r.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="typo-label text-[color:var(--color-text-lo)]">
                  Defines which data this user can access within the business.
                </p>
              </div>

              <div className="space-y-2">
                <label className="typo-label text-[color:var(--color-text-md)]">
                  Assign to Businesses
                </label>
                {businesses.length === 0 ? (
                  <p className="typo-body text-[color:var(--color-text-lo)]">
                    No businesses found.
                  </p>
                ) : (
                  <div className="space-y-2">
                    {businesses.map((b) => (
                      <label
                        key={b.id}
                        className="flex items-center gap-2.5 cursor-pointer"
                      >
                        <Checkbox
                          checked={selectedBusinessIds.includes(b.id)}
                          onCheckedChange={(checked) =>
                            toggleBusiness(b.id, !!checked)
                          }
                        />
                        <span className="typo-body text-[color:var(--color-text-hi)]">
                          {b.name}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {error && <p className="typo-body text-destructive">{error}</p>}
        </div>

        <SheetFooter className="border-t pt-4">
          <Button variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              submitting ||
              !email.trim() ||
              (userType === "member" && selectedBusinessIds.length === 0)
            }
          >
            {submitting ? "Inviting..." : "Send Invite"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
